"use client";
import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Database, Globe, Lock, AppWindow, CreditCard, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

/* Icon and wheel position per benefit. Index-aligned with
   `multiSchool.benefits` in the dictionary — the angle decides where the label
   sits on the donut, so it can never be translator-editable. */
const BENEFIT_PLACEMENT = [
  { icon: Lock, angle: 120 },        // Top Left
  { icon: AppWindow, angle: 60 },    // Top Right
  { icon: Database, angle: 0 },      // Right
  { icon: Users, angle: 300 },       // Bottom Right
  { icon: CreditCard, angle: 240 },  // Bottom Left
  { icon: Globe, angle: 180 },       // Left
];

export default function MultiSchool() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const { t, tv } = useLanguage();

  const benefits = tv<{ title: string; desc: string }[]>("multiSchool.benefits").map(
    (b, i) => ({ ...b, ...BENEFIT_PLACEMENT[i] })
  );

  // Helper to draw donut wedges in SVG with rounded coordinates for SSR hydration consistency
  const describeWedge = (cx: number, cy: number, rInner: number, rOuter: number, startAngle: number, endAngle: number) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = (cx + rOuter * Math.cos(startRad)).toFixed(2);
    const y1 = (cy - rOuter * Math.sin(startRad)).toFixed(2);
    const x2 = (cx + rOuter * Math.cos(endRad)).toFixed(2);
    const y2 = (cy - rOuter * Math.sin(endRad)).toFixed(2);

    const x3 = (cx + rInner * Math.cos(endRad)).toFixed(2);
    const y3 = (cy - rInner * Math.sin(endRad)).toFixed(2);
    const x4 = (cx + rInner * Math.cos(startRad)).toFixed(2);
    const y4 = (cy - rInner * Math.sin(startRad)).toFixed(2);

    return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 0 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 0 1 ${x4} ${y4} Z`;
  };

  const wedges = [
    { angle: 120, color: "#3D5EE1" }, // Top-Left (Royal Blue)
    { angle: 60, color: "#60A5FA" }, // Top-Right (Sky Blue)
    { angle: 0, color: "#3B82F6" }, // Right (Electric Blue)
    { angle: 300, color: "#93C5FD" }, // Bottom-Right (Light Royal Blue)
    { angle: 240, color: "#818CF8" }, // Bottom-Left (Indigo Blue)
    { angle: 180, color: "#A5B4FC" }, // Left (Soft Indigo)
  ];

  return (
    <section ref={ref} id="multi-school" className="w-full scroll-mt-32 pt-20 sm:pt-24 lg:pt-16 pb-10 sm:pb-12 lg:pb-16 bg-gradient-to-br from-[#EFF3FE] via-[#F8FAFC] to-[#EFF3FE] overflow-hidden relative">

      {/* Abstract Background Lighting */}
      <div className="absolute top-0 right-0 w-[500px] sm:w-[800px] h-[500px] sm:h-[800px] bg-[#2A48C9]/[0.06] rounded-full blur-[100px] sm:blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-[#2A48C9]/[0.07] rounded-full blur-[100px] sm:blur-[120px] pointer-events-none -translate-x-1/3 translate-y-1/3" />

      <div className="site-container relative z-10">
        <div className="flex flex-col xl:flex-row gap-8 xl:gap-8 items-center">

          {/* ── Left Column ── */}
          <div className="w-full xl:w-[52%] flex flex-col items-center xl:items-start text-center xl:text-left">
            <h2 className="font-heading section-heading text-[#0d1117] mb-4 sm:mb-8 xl:whitespace-nowrap">
              <RichText text={t("multiSchool.heading")} breakClassName="hidden sm:block" />
            </h2>

            <p className="text-[15px] sm:text-[17px] lg:text-[18px] text-[#596875] leading-relaxed font-medium max-w-[500px] xl:max-w-[620px]">
              {/* Body copy, so the accent is a plain brand-blue run rather than
                  the gradient `section-heading-accent` used on headings. */}
              <RichText text={t("multiSchool.body")} accentClassName="text-[#3D5EE1]" />
            </p>

          </div>

          {/* ── Donut Wheel Diagram with Floating Labels (Unified for All Mobile & Desktop Screens) ── */}
          <div className="w-full xl:w-[48%] flex flex-col items-center justify-center mt-6 xl:mt-0 relative overflow-hidden sm:overflow-visible">
            
            {/* Responsive Scaled Stage Container */}
            <div className="w-[700px] h-[480px] relative flex items-center justify-center shrink-0 my-[-110px] min-[360px]:my-[-95px] min-[400px]:my-[-80px] sm:my-[-40px] lg:my-[48px] xl:my-0 2xl:my-[20px] scale-[0.44] min-[360px]:scale-[0.48] min-[390px]:scale-[0.52] min-[430px]:scale-[0.58] sm:scale-[0.78] md:scale-100 lg:scale-[1.2] xl:scale-100 2xl:scale-[1.1] origin-center transition-transform duration-300">

              {/* Shared 360×360 anchor box — wheel, icons and labels all resolve
                  against this same 500-unit coordinate space so connectors line up. */}
              <div className="relative w-[360px] h-[360px]">

                {/* Soft halo behind the wheel */}
                <div aria-hidden className="absolute inset-[-14%] rounded-full bg-[radial-gradient(circle,rgba(61,94,225,0.10)_0%,transparent_68%)] pointer-events-none" />

              {/* Main Donut Wheel SVG */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, type: "spring", bounce: 0.15 }}
                className="absolute inset-0 z-10 flex items-center justify-center"
              >
                <svg viewBox="0 0 500 500" className="w-full h-full drop-shadow-xl overflow-visible" fill="none" role="img" aria-label={t("multiSchool.diagramAria")}>
                  {/* Donut Segments */}
                  {wedges.map((w, idx) => {
                    const startAngle = w.angle - 26;
                    const endAngle = w.angle + 26;
                    const pathStr = describeWedge(250, 250, 95, 185, startAngle, endAngle);
                    const isHovered = hoveredIdx === idx;
                    const isDimmed = hoveredIdx !== null && !isHovered;

                    return (
                      <g
                        key={idx}
                        className="cursor-pointer"
                        onMouseEnter={() => setHoveredIdx(idx)}
                        onMouseLeave={() => setHoveredIdx(null)}
                      >
                        <path
                          d={pathStr}
                          fill={w.color}
                          className="transition-all duration-300 origin-center"
                          style={{
                            transformOrigin: "250px 250px",
                            transform: isHovered ? "scale(1.06)" : "scale(1)",
                            opacity: isDimmed ? 0.55 : 1,
                            filter: isHovered ? "brightness(1.08)" : "none"
                          }}
                        />
                      </g>
                    );
                  })}

                  {/* Center White Circle */}
                  <circle cx="250" cy="250" r="85" fill="#FFFFFF" className="drop-shadow-lg" />
                  <circle cx="250" cy="250" r="89" fill="none" stroke="#2A48C9" strokeOpacity="0.14" strokeWidth="1.5" />

                  {/* Kaympus "K" Logo Mark in Center. The real brand asset rather
                      than a hand-built approximation — it ships with a transparent
                      background, so it sits straight on the white centre circle.
                      x/y are nudged 2px off dead-centre because the mark itself
                      sits slightly left of, and low in, its own square canvas. */}
                  <image
                    href="/favicon.svg"
                    x="200"
                    y="196"
                    width="105"
                    height="105"
                  />

                  {/* Dotted Connecting Lines */}
                  {benefits.map((b, i) => {
                    const rad = (b.angle * Math.PI) / 180;
                    const xStart = Number((250 + 185 * Math.cos(rad)).toFixed(2));
                    const yStart = Number((250 - 185 * Math.sin(rad)).toFixed(2));

                    const xEnd = Number((250 + 258 * Math.cos(rad)).toFixed(2));
                    const yEnd = Number((250 - 258 * Math.sin(rad)).toFixed(2));
                    const isHovered = hoveredIdx === i;
                    const isDimmed = hoveredIdx !== null && !isHovered;

                    return (
                      <g key={i} className="transition-opacity duration-300" opacity={isDimmed ? 0.45 : 1}>
                        <line
                          x1={xStart}
                          y1={yStart}
                          x2={xEnd}
                          y2={yEnd}
                          stroke={isHovered ? "#3D5EE1" : "#2A48C9"}
                          strokeWidth={isHovered ? "2.5" : "2"}
                          strokeDasharray={isHovered ? "none" : "3 3"}
                          opacity={isHovered ? 1 : 0.6}
                          className="transition-all duration-300"
                        />
                        <circle cx={xEnd} cy={yEnd} r={isHovered ? 4 : 3} fill="#2A48C9" className="transition-all duration-300" />
                      </g>
                    );
                  })}
                </svg>
              </motion.div>

              {/* Icons inside the Donut Segments */}
              {benefits.map((b, i) => {
                const rad = (b.angle * Math.PI) / 180;
                const iconRadius = 140;
                const x = Number((250 + iconRadius * Math.cos(rad)).toFixed(2));
                const y = Number((250 - iconRadius * Math.sin(rad)).toFixed(2));
                const isHovered = hoveredIdx === i;

                const Icon = b.icon;

                return (
                  // Outer div owns positioning only. Framer Motion writes `transform`
                  // on the elements it animates, so the two must never share a node.
                  <div
                    key={i}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    className="absolute z-20 cursor-pointer -translate-x-1/2 -translate-y-1/2"
                    style={{
                      left: `${(x / 500) * 100}%`,
                      top: `${(y / 500) * 100}%`,
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={isInView ? { opacity: 1, scale: isHovered ? 1.25 : 1 } : {}}
                      transition={{ duration: 0.5, delay: isInView && isHovered ? 0 : 0.35 + i * 0.07 }}
                    >
                      <Icon size={22} className="text-white drop-shadow-md" strokeWidth={2} aria-hidden />
                    </motion.div>
                  </div>
                );
              })}

              {/* Floating Text Labels */}
              {benefits.map((b, i) => {
                const rad = (b.angle * Math.PI) / 180;
                // 280 units ≈ 202px in the 360px box — keeps every label corner
                // clear of the 185-unit wedge edge, including the diagonal ones.
                const labelRadius = 280;
                const x = Number((250 + labelRadius * Math.cos(rad)).toFixed(2));
                const y = Number((250 - labelRadius * Math.sin(rad)).toFixed(2));

                const isLeft = b.angle > 90 && b.angle < 270;
                const isHovered = hoveredIdx === i;

                return (
                  // Outer div owns positioning only — see the icon note above.
                  <div
                    key={i}
                    tabIndex={0}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    onFocus={() => setHoveredIdx(i)}
                    onBlur={() => setHoveredIdx(null)}
                    className={`absolute z-30 w-[128px] min-[360px]:w-[136px] sm:w-[128px] cursor-pointer rounded-lg outline-none
                                focus-visible:ring-2 focus-visible:ring-[#2A48C9]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F6FCFB]
                                ${isLeft ? 'text-right' : 'text-left'}`}
                    style={{
                      left: `${(x / 500) * 100}%`,
                      top: `${(y / 500) * 100}%`,
                      transform: `translate(${isLeft ? 'calc(-100% - 4px)' : '4px'}, -50%)`,
                    }}
                  >
                    {/* Below `sm` the whole stage is scaled to 0.44-0.58, so the
                        13.5/11.5px labels render at an effective ~6px. The max-sm
                        overrides compensate: larger, heavier, darker type on a
                        white chip so the labels stay legible against the mint
                        background at that scale. */}
                    <motion.div
                      initial={{ opacity: 0, x: isLeft ? 10 : -10 }}
                      animate={isInView ? { opacity: hoveredIdx !== null && !isHovered ? 0.6 : 1, x: 0, scale: isHovered ? 1.06 : 1 } : {}}
                      transition={{ duration: 0.6, delay: isInView && hoveredIdx !== null ? 0 : 0.45 + (i * 0.09) }}
                      style={{ transformOrigin: isLeft ? "right center" : "left center" }}
                      className="max-sm:rounded-xl max-sm:bg-white/85 max-sm:px-1.5 max-sm:py-2
                                 max-sm:ring-1 max-sm:ring-[#2A48C9]/25
                                 max-sm:shadow-[0_10px_24px_-8px_rgba(61,94,225,0.35)]"
                    >
                      <h3 className={`font-extrabold sm:font-bold text-[18px] sm:text-[13.5px] leading-tight mb-1 sm:mb-0.5 transition-colors duration-300 ${isHovered ? 'text-[#3D5EE1]' : 'text-[#0d1117]'}`}>
                        {b.title}
                      </h3>
                      <p className="text-[14.5px] sm:text-[11.5px] text-[#3f4c5c] sm:text-[#64748b] font-semibold sm:font-medium leading-[1.3] sm:leading-[1.35] break-words">
                        {b.desc}
                      </p>
                    </motion.div>
                  </div>
                );
              })}

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

